import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Slider } from "../../components/ui/slider";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  ArrowLeft,
  Save,
  Palette,
  ChevronDown,
  ChevronUp,
  Image,
  Video,
  Box,
  Eye,
  Plus,
  Minus,
  Calculator,
  FileText,
  Home,
  Clock,
  Building2,
  MapPin,
  X,
  Mountain,
  Trees,
  Repeat,
  Trash2,
  Sparkles,
  Film,
  Check,
  CheckCircle2,
  AlertTriangle,
  Paintbrush,
  HelpCircle,
  Info
} from "lucide-react";
import { TimelineEstimateWidget } from "../../components/calculators/TimelineEstimateWidget";
import { calculateInteriorModeling, INTERIOR_MODELING_TIERS, normalizeTiers } from "./interiorModelingPricing";
import VideoAnimationPicker from "../../components/calculators/VideoAnimationPicker";
import {
  VIDEO_ANIMATION_TYPES,
  SOCIAL_MEDIA_PACK,
  getAnimationPrice,
  getAnimationShots,
  getAnimationDisplayName,
  getAnimationIncludes,
} from "./videoAnimationPricing";

const SALESPEOPLE = ["Jordan", "Julian", "Cole", "Bernard"];

// VR Tour Configuration - Residential
const RESIDENTIAL_VR_TOURS = [
  { key: "studio", label: "Studio", includedPoints: 4, rateKey: "studio" },
  { key: "1bed", label: "1 Bedroom", includedPoints: 5, rateKey: "1 bedroom" },
  { key: "2bed", label: "2 Bedroom", includedPoints: 6, rateKey: "2 bedroom" },
  { key: "3bed", label: "3+ Bedroom", includedPoints: 8, rateKey: "3 bedroom" }
];

// VR Tour Configuration - Amenity
const AMENITY_VR_TOURS = [
  { key: "small", label: "Small", includedPoints: 4, rateKey: "amenity_small" },
  { key: "medium", label: "Medium", includedPoints: 6, rateKey: "amenity_medium" },
  { key: "large", label: "Large", includedPoints: 8, rateKey: "amenity_large" },
  { key: "xl", label: "XL", includedPoints: 10, rateKey: "amenity_xl" }
];

// Default VR rates
const DEFAULT_VR_RATES = {
  studio: 14000,
  "1 bedroom": 18000,
  "2 bedroom": 20000,
  "3 bedroom": 22000,
  amenity_small: 12000,
  amenity_medium: 16000,
  amenity_large: 20000,
  amenity_xl: 24000,
  additional_point: 750
};

// Customizer Category Types
const CUSTOMIZER_CATEGORY_TYPES = [
  { value: "flooring", label: "Flooring" },
  { value: "cabinets", label: "Cabinets" },
  { value: "countertops", label: "Countertops" },
  { value: "backsplash", label: "Backsplash" },
  { value: "appliance_package", label: "Appliance Package" },
  { value: "plumbing_fixtures", label: "Plumbing Fixtures" },
  { value: "furniture_package", label: "Furniture Package" },
  { value: "paint_wall_finish", label: "Paint / Wall Finish" },
  { value: "lighting_package", label: "Lighting Package" },
  { value: "custom", label: "Custom Category" }
];

// Default Customizer Pricing (fallback)
const DEFAULT_CUSTOMIZER_PRICING = {
  baseFee: 2500,
  optionPriceTier1: 800,
  optionPriceTier2: 700,
  optionPriceTier3Plus: 500,
  furnitureTier1: 1800,
  furnitureTier2: 1500,
  furnitureTier3: 1200,
  furnitureTier4Plus: 1000
};

// Interior Design Direction Configuration
const DESIGN_COMPLETENESS_LEVELS = [
  { 
    value: "full_package", 
    label: "Fully Developed Package", 
    fee: 0,
    shortLabel: "Full Package",
    description: "Client has a complete interior design package and TRP is replicating a defined design.",
    assets: [
      "Finish schedules",
      "Furniture layout plans",
      "Furniture selections",
      "Interior elevations (kitchen, bathrooms, millwork)",
      "Lighting plans",
      "Material specifications"
    ],
    questions: [
      "Does the client have finish schedules?",
      "Are there interior elevations?",
      "Is there a furniture layout?",
      "Are specific materials selected?",
      "Is there an interior designer package?"
    ],
    trpImpact: "Faster production, fewer revisions, pricing remains standard."
  },
  { 
    value: "partial", 
    label: "Partial Interior Design", 
    fee: 1000,
    shortLabel: "Partial Design",
    description: "Client has some direction but no complete package.",
    assets: [
      "Pinterest boards",
      "Reference images",
      "Mood boards",
      "Material inspiration",
      "Rough style direction"
    ],
    questions: [
      "Do you have a full design package or just inspiration?",
      "Are the materials final or just ideas?",
      "Is an interior designer involved?",
      "Are furniture pieces specified or flexible?"
    ],
    trpImpact: "Conceptual interpretation required, more revisions, more creative design work."
  },
  { 
    value: "concept_creation", 
    label: "Full Concept Creation", 
    fee: 2000,
    shortLabel: "Concept Creation",
    description: "Client has no real interior design defined and expects TRP to create the look and direction.",
    assets: [
      '"Make it look modern / luxury / nice"',
      "No designer involvement",
      "General style preferences only"
    ],
    questions: [
      "What style are you aiming for?",
      "Do you have reference projects?",
      "Are there brands or designers you like?",
      "Do you have any inspiration at all?"
    ],
    trpImpact: "Full concept development, furniture selection, material selection, design creation from scratch, more revision risk, higher creative responsibility."
  }
];

// Exterior Modeling Complexity Tiers
const COMPLEXITY_TIERS = [
  { value: "small", label: "Small", description: "Cottage, small home" },
  { value: "medium", label: "Medium", description: "Townhome, mid-rise" },
  { value: "large", label: "Large", description: "High-rise tower" },
  { value: "xl", label: "XL", description: "Luxury tower, resort" }
];

// Site Environment Options
const SITE_ENVIRONMENTS = [
  { value: "none", label: "None", description: "No site modeling needed" },
  { value: "simple", label: "Simple", description: "Minimal roads, basic landscape" },
  { value: "standard", label: "Standard", description: "Roads, sidewalks, landscape" },
  { value: "complex", label: "Complex", description: "Pools, waterfront, terrain" },
  { value: "masterplan", label: "Masterplan", description: "Golf course, marina, large community" }
];

// Default exterior modeling rates
const DEFAULT_EXT_RATES = {
  small: 12000,
  medium: 15000,
  large: 18000,
  xl: 25000,
  replication: 500,
  site_simple: 2000,
  site_standard: 5000,
  site_complex: 10000,
  site_masterplan: 15000,
  reality_capture: 8500
};

// Create empty exterior structure row
function createEmptyStructure() {
  return {
    id: crypto.randomUUID(),
    name: "",
    complexity: "medium",
    uniqueCount: 1,
    repeatCount: 0,
    notes: ""
  };
}

// VR Tour Card Component with Real-Time Customizer
function VRTourCard({ 
  config, 
  quantity, 
  setQuantity, 
  additionalPoints, 
  setAdditionalPoints, 
  rate, 
  totalCost,
  customizerEnabled,
  setCustomizerEnabled,
  customizerCategories,
  setCustomizerCategories,
  customizerPricing,
  customizerCalc
}) {
  const finalPoints = config.includedPoints + additionalPoints;
  
  // Add a new category to the customizer
  const addCategory = () => {
    const newCategory = {
      id: Date.now(),
      type: "flooring",
      customName: "",
      optionCount: 3
    };
    setCustomizerCategories([...customizerCategories, newCategory]);
  };
  
  // Update a category
  const updateCategory = (id, field, value) => {
    setCustomizerCategories(customizerCategories.map(cat => 
      cat.id === id ? { ...cat, [field]: value } : cat
    ));
  };
  
  // Remove a category
  const removeCategory = (id) => {
    setCustomizerCategories(customizerCategories.filter(cat => cat.id !== id));
  };
  
  return (
    <div className={`p-4 rounded-xl border-2 transition-all ${quantity > 0 ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border hover:border-muted-foreground/30'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold">{config.label}</h4>
          <p className="text-xs text-muted-foreground">{config.includedPoints} points included</p>
        </div>
        {quantity > 0 && (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            {finalPoints} pts
          </Badge>
        )}
      </div>
      
      {/* Quantity Control */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Quantity</Label>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => {
                const newQty = Math.max(0, quantity - 1);
                setQuantity(newQty);
                if (newQty === 0 && customizerEnabled) {
                  setCustomizerEnabled(false);
                  setCustomizerCategories([]);
                }
              }}
            >
              <Minus className="w-3 h-3" />
            </Button>
            <span className="w-8 text-center font-medium">{quantity}</span>
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => setQuantity(quantity + 1)}
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Additional Points Control */}
        {quantity > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Additional Points</Label>
              <span className="text-xs font-medium">+{additionalPoints}</span>
            </div>
            <Slider
              value={[additionalPoints]}
              onValueChange={(v) => setAdditionalPoints(v[0])}
              min={0}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>+5</span>
            </div>
          </div>
        )}

        {/* Base Cost Display */}
        {quantity > 0 && (
          <div className="pt-2 border-t flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {quantity} × ${rate.toLocaleString()}
              {additionalPoints > 0 && ` + ${quantity * additionalPoints} pts`}
            </span>
            <span className="font-semibold text-emerald-600">${totalCost.toLocaleString()}</span>
          </div>
        )}

        {/* Real-Time Customizer Add-On */}
        {quantity > 0 && (
          <div className="pt-3 mt-1 border-t border-dashed space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <Label className="text-xs font-medium text-amber-600">Real-Time Customizer</Label>
              </div>
              <Switch
                checked={customizerEnabled}
                onCheckedChange={(checked) => {
                  setCustomizerEnabled(checked);
                  if (checked && customizerCategories.length === 0) {
                    addCategory();
                  }
                }}
                data-testid={`customizer-toggle-${config.key}`}
              />
            </div>
            
            {customizerEnabled && (
              <div className="space-y-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <p className="text-xs text-muted-foreground">
                  Configure real-time finish, furniture, material, and upgrade selections for this tour.
                </p>
                
                {/* Base Fee Display */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Base Activation Fee</span>
                  <span className="font-medium">${customizerPricing.baseFee.toLocaleString()}</span>
                </div>
                
                {/* Categories */}
                <div className="space-y-2">
                  {customizerCategories.map((cat, idx) => {
                    // Tiered option pricing - check for Furniture Package
                    let pricePerOption;
                    const isFurniture = cat.type === "furniture_package";
                    
                    if (isFurniture) {
                      if (cat.optionCount === 1) pricePerOption = customizerPricing.furnitureTier1;
                      else if (cat.optionCount === 2) pricePerOption = customizerPricing.furnitureTier2;
                      else if (cat.optionCount === 3) pricePerOption = customizerPricing.furnitureTier3;
                      else pricePerOption = customizerPricing.furnitureTier4Plus;
                    } else {
                      if (cat.optionCount === 1) pricePerOption = customizerPricing.optionPriceTier1;
                      else if (cat.optionCount === 2) pricePerOption = customizerPricing.optionPriceTier2;
                      else pricePerOption = customizerPricing.optionPriceTier3Plus;
                    }
                    const categoryTotal = cat.optionCount * pricePerOption;
                    
                    return (
                      <div key={cat.id} className={`p-2 rounded-md space-y-2 ${isFurniture ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-background/60'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground w-6">#{idx + 1}</span>
                          <Select 
                            value={cat.type} 
                            onValueChange={(v) => updateCategory(cat.id, "type", v)}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CUSTOMIZER_CATEGORY_TYPES.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7" 
                            onClick={() => removeCategory(cat.id)}
                          >
                            <X className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                        
                        {cat.type === "custom" && (
                          <Input
                            placeholder="Custom category name..."
                            value={cat.customName}
                            onChange={(e) => updateCategory(cat.id, "customName", e.target.value)}
                            className="h-8 text-xs"
                          />
                        )}
                        
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Options</Label>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => updateCategory(cat.id, "optionCount", Math.max(1, cat.optionCount - 1))}
                            >
                              <Minus className="w-2 h-2" />
                            </Button>
                            <span className="w-6 text-center text-xs font-medium">{cat.optionCount}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => updateCategory(cat.id, "optionCount", cat.optionCount + 1)}
                            >
                              <Plus className="w-2 h-2" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex justify-between text-xs">
                          <span className={isFurniture ? "text-purple-600" : "text-muted-foreground"}>
                            {cat.optionCount} × ${pricePerOption.toLocaleString()}/option
                            {isFurniture && " (premium)"}
                          </span>
                          <span className={`font-medium ${isFurniture ? "text-purple-600" : ""}`}>${categoryTotal.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full h-8 text-xs gap-1"
                    onClick={addCategory}
                  >
                    <Plus className="w-3 h-3" />
                    Add Category
                  </Button>
                </div>
                
                {/* Customizer Total */}
                <div className="pt-2 border-t flex items-center justify-between">
                  <span className="text-xs font-medium">Customizer Total</span>
                  <span className="font-semibold text-amber-600">${customizerCalc.total.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Visualization Payment Plan — milestone structures (matches Combined Calculator)
const PAYMENT_PRESETS = {
  "50-50": {
    label: "50 / 50",
    milestones: [
      { percent: 50, label: "Due at Signing" },
      { percent: 50, label: "Due at Project Milestone" }
    ]
  },
  "40-30-30": {
    label: "40 / 30 / 30",
    milestones: [
      { percent: 40, label: "Due at Signing" },
      { percent: 30, label: "Due at Midpoint Review" },
      { percent: 30, label: "Due Prior to Final Delivery" }
    ]
  },
  "50-25-25": {
    label: "50 / 25 / 25",
    milestones: [
      { percent: 50, label: "Due at Signing" },
      { percent: 25, label: "Due at Midpoint" },
      { percent: 25, label: "Due at Final Delivery" }
    ]
  }
};

export default function VisualizationCalculator() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const isNew = quoteId === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [pricingRules, setPricingRules] = useState([]);
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const [proposalHtml, setProposalHtml] = useState(null);
  
  // Dynamic tiers from pricing rules
  const [complexityTiers, setComplexityTiers] = useState(COMPLEXITY_TIERS);
  const [siteEnvironments, setSiteEnvironments] = useState(SITE_ENVIRONMENTS);
  
  // Form state
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [notes, setNotes] = useState("");
  
  // Visualization Payment Plan (milestone-based)
  const [paymentPreset, setPaymentPreset] = useState("50-50");
  
  // Interior Modeling inputs (no longer exterior here - moved to Exterior Modeling section)
  const [interiorSqft, setInteriorSqft] = useState(0);
  
  // Amenity Interior Modeling — sqft-based with complexity (parity with Combined Calculator)
  const [amenityEntries, setAmenityEntries] = useState([]);
  const [amenityBaseRate, setAmenityBaseRate] = useState(1.75);
  // Admin-editable interior modeling tiers
  const [interiorTiers, setInteriorTiers] = useState(INTERIOR_MODELING_TIERS);

  // Reusable tier fetcher — called on mount AND on window focus so admin edits are reflected without full reload
  const refetchInteriorTiers = async () => {
    try {
      const tierRes = await apiClient.get("/calculators/interior-modeling-tiers");
      if (Array.isArray(tierRes.data) && tierRes.data.length > 0) {
        setInteriorTiers(normalizeTiers(tierRes.data));
      }
    } catch (e) { /* fallback to defaults */ }
  };

  useEffect(() => {
    const handler = () => { refetchInteriorTiers(); };
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, []);
  const [amenityComplexityMultipliers, setAmenityComplexityMultipliers] = useState({
    standard: 1.0, premium: 1.10, signature: 1.20
  });
  
  const addAmenity = () => setAmenityEntries(prev => [...prev, {
    id: `am-${Date.now()}`, name: "", sqft: 0, complexity: "standard",
    modelingType: "interior_existing_structure", structureSize: "medium", sharedGroupId: ""
  }]);
  const removeAmenity = (id) => setAmenityEntries(prev => prev.filter(a => a.id !== id));
  const updateAmenity = (id, field, value) => setAmenityEntries(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  
  // Exterior Modeling inputs (NEW modular system)
  const [exteriorStructures, setExteriorStructures] = useState([createEmptyStructure()]);
  const [siteEnvironment, setSiteEnvironment] = useState("none");
  const [includeRealityCapture, setIncludeRealityCapture] = useState(false);
  const [realityCaptureRate, setRealityCaptureRate] = useState(8500);
  
  // Rendering inputs
  const [interiorRenders, setInteriorRenders] = useState(0);
  const [exteriorRenders, setExteriorRenders] = useState(0);
  const [aerialRenders, setAerialRenders] = useState(0);
  const [aerialRate, setAerialRate] = useState(6000);
  const [floorPlans, setFloorPlans] = useState(0);
  const [floorPlanRate, setFloorPlanRate] = useState(1000);
  
  // VR Tour inputs - Residential
  const [vrResidential, setVrResidential] = useState({
    studio: { quantity: 0, additionalPoints: 0 },
    "1bed": { quantity: 0, additionalPoints: 0 },
    "2bed": { quantity: 0, additionalPoints: 0 },
    "3bed": { quantity: 0, additionalPoints: 0 }
  });
  
  // VR Tour inputs - Amenity
  const [vrAmenity, setVrAmenity] = useState({
    small: { quantity: 0, additionalPoints: 0 },
    medium: { quantity: 0, additionalPoints: 0 },
    large: { quantity: 0, additionalPoints: 0 },
    xl: { quantity: 0, additionalPoints: 0 }
  });
  
  // Additional point rate
  const [additionalPointRate, setAdditionalPointRate] = useState(750);
  
  // VR Tour Real-Time Customizer state (per tour type)
  const [vrResidentialCustomizer, setVrResidentialCustomizer] = useState({
    studio: { enabled: false, categories: [] },
    "1bed": { enabled: false, categories: [] },
    "2bed": { enabled: false, categories: [] },
    "3bed": { enabled: false, categories: [] }
  });
  
  const [vrAmenityCustomizer, setVrAmenityCustomizer] = useState({
    small: { enabled: false, categories: [] },
    medium: { enabled: false, categories: [] },
    large: { enabled: false, categories: [] },
    xl: { enabled: false, categories: [] }
  });
  
  // Dynamic customizer pricing from backend
  const [customizerPricing, setCustomizerPricing] = useState(DEFAULT_CUSTOMIZER_PRICING);
  
  // Video Animation inputs - FIXED PRICING MODEL
  // Two product types: 'ai' | 'cinematic'. Social Media Pack is an optional add-on.
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const [animationType, setAnimationType] = useState("cinematic"); // 'ai' | 'cinematic'
  const [animationDuration, setAnimationDuration] = useState(60); // 30, 60, or 90 seconds
  const [animationModelingAligned, setAnimationModelingAligned] = useState(null); // null, true, or false
  const [socialMediaPack, setSocialMediaPack] = useState(false);
  
  // Fixed pricing tiers imported from shared module (AI Motion + Cinematic Film)
  // Legacy fallback for any stored quote that didn't track animation_type
  const ANIMATION_PRICING = VIDEO_ANIMATION_TYPES.cinematic.pricing;
  
  // Interior Design Direction inputs
  const [interiorDesignSpacesOverride, setInteriorDesignSpacesOverride] = useState(null);
  const [interiorDesignSpaces, setInteriorDesignSpaces] = useState([]);
  const [showDesignGuidance, setShowDesignGuidance] = useState(false);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    interiorModeling: true,
    exteriorModeling: true,
    rendering: true,
    vr: true,
    animation: true
  });

  // Load pricing rules and quote data
  useEffect(() => {
    async function loadData() {
      try {
        // Load both visualization AND vexos rules (for exterior modeling)
        const [vizRes, vexosRes] = await Promise.all([
          apiClient.get("/calculators/pricing-rules?calculator_type=visualization"),
          apiClient.get("/calculators/pricing-rules?calculator_type=vexos")
        ]);
        const allRules = [...vizRes.data, ...vexosRes.data];
        setPricingRules(allRules);
        
        // Load dynamic complexity tiers from pricing rules
        const complexityFromRules = allRules
          .filter(r => r.category === "complexity" && r.active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(r => ({
            value: r.tier_config?.tier_value || r.label.toLowerCase().split(' ')[0],
            label: r.label.split(' (')[0],
            description: r.description,
            rate: r.default_rate
          }));
        if (complexityFromRules.length > 0) {
          setComplexityTiers(complexityFromRules);
        }
        
        // Load dynamic site environments from pricing rules
        const siteEnvFromRules = allRules
          .filter(r => r.category === "site_environment" && r.active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(r => ({
            value: r.tier_config?.tier_value || r.label.toLowerCase(),
            label: r.label,
            description: r.description,
            rate: r.default_rate
          }));
        if (siteEnvFromRules.length > 0) {
          setSiteEnvironments(siteEnvFromRules);
        }
        
        // Get additional point rate from rules
        const pointRule = vizRes.data.find(r => r.label.toLowerCase().includes("additional point"));
        if (pointRule) setAdditionalPointRate(pointRule.default_rate);
        
        // Get aerial rendering rate from rules
        const aerialRule = vizRes.data.find(r => r.label.toLowerCase().includes("aerial"));
        if (aerialRule) setAerialRate(aerialRule.default_rate);
        
        // Get floor plan rate from rules
        const fpRule = vizRes.data.find(r => r.label.toLowerCase().includes("floor plan"));
        if (fpRule) setFloorPlanRate(fpRule.default_rate);
        
        // Get amenity sqft base rate + complexity multipliers from pricing rules (new model)
        const amenitySqftRule = allRules.find(r => r.category === "amenity_sqft" && r.active);
        if (amenitySqftRule) setAmenityBaseRate(amenitySqftRule.default_rate);
        const complexityRules = allRules.filter(r => r.category === "amenity_complexity" && r.active);
        if (complexityRules.length > 0) {
          const mults = { standard: 1.0, premium: 1.10, signature: 1.20 };
          complexityRules.forEach(r => {
            const key = (r.label || "").toLowerCase();
            if (key.includes("standard")) mults.standard = r.default_rate;
            else if (key.includes("premium")) mults.premium = r.default_rate;
            else if (key.includes("signature")) mults.signature = r.default_rate;
          });
          setAmenityComplexityMultipliers(mults);
        }
        // Fetch admin-editable interior modeling tiers
        await refetchInteriorTiers();
        
        // Get reality capture rate from rules
        const rcRule = vexosRes.data.find(r => r.label.toLowerCase().includes("reality capture"));
        if (rcRule) setRealityCaptureRate(rcRule.default_rate);
        
        // Load Customizer pricing from rules
        const customizerBaseRule = allRules.find(r => r.category === "customizer_base");
        const customizerTier1Rule = allRules.find(r => r.category === "customizer_option_tier1");
        const customizerTier2Rule = allRules.find(r => r.category === "customizer_option_tier2");
        const customizerTier3Rule = allRules.find(r => r.category === "customizer_option_tier3plus");
        const furnitureTier1Rule = allRules.find(r => r.category === "customizer_furniture_tier1");
        const furnitureTier2Rule = allRules.find(r => r.category === "customizer_furniture_tier2");
        const furnitureTier3Rule = allRules.find(r => r.category === "customizer_furniture_tier3");
        const furnitureTier4Rule = allRules.find(r => r.category === "customizer_furniture_tier4plus");
        
        setCustomizerPricing({
          baseFee: customizerBaseRule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.baseFee,
          optionPriceTier1: customizerTier1Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.optionPriceTier1,
          optionPriceTier2: customizerTier2Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.optionPriceTier2,
          optionPriceTier3Plus: customizerTier3Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.optionPriceTier3Plus,
          furnitureTier1: furnitureTier1Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier1,
          furnitureTier2: furnitureTier2Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier2,
          furnitureTier3: furnitureTier3Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier3,
          furnitureTier4Plus: furnitureTier4Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier4Plus
        });
        
        if (!isNew) {
          const quoteRes = await apiClient.get(`/calculators/quotes/${quoteId}`);
          const quote = quoteRes.data;
          
          setProjectName(quote.project_name || "");
          setClientName(quote.client_name || "");
          setSalesperson(quote.salesperson || "");
          setNotes(quote.notes || "");
          // Restore payment plan preset
          if (quote.payment_plan?.preset && PAYMENT_PRESETS[quote.payment_plan.preset]) {
            setPaymentPreset(quote.payment_plan.preset);
          }
          
          // Extract values from sections
          quote.sections?.forEach(section => {
            const sectionType = section.section_type || "";
            const sectionMeta = section.metadata || {};
            
            // Handle exterior modeling section
            if (sectionType === "viz_exterior_modeling") {
              const meta = sectionMeta;
              setSiteEnvironment(meta.site_environment || "none");
              setIncludeRealityCapture(meta.reality_capture_enabled || false);
              
              // Restore structures from line items
              const structures = [];
              section.line_items?.forEach(item => {
                const itemMeta = item.metadata || {};
                if (itemMeta.item_type === "structure") {
                  structures.push({
                    id: item.id || crypto.randomUUID(),
                    name: itemMeta.structure_name || "",
                    complexity: itemMeta.complexity_tier || "medium",
                    uniqueCount: itemMeta.unique_count || 0,
                    repeatCount: itemMeta.repeat_count || 0,
                    notes: itemMeta.notes || ""
                  });
                }
              });
              if (structures.length > 0) {
                setExteriorStructures(structures);
              }
            }
            
            // Handle animation section
            if (sectionType === "viz_animation") {
              const meta = sectionMeta;
              if (section.line_items?.length > 0) {
                setAnimationEnabled(true);
                if (meta.animation_type) setAnimationType(meta.animation_type);
                if (meta.duration_seconds) setAnimationDuration(meta.duration_seconds);
                if (meta.modeling_aligned !== null && meta.modeling_aligned !== undefined) {
                  setAnimationModelingAligned(meta.modeling_aligned);
                }
                if (meta.social_media_pack === true) setSocialMediaPack(true);
                // Fallback: detect social media pack via a dedicated line item if metadata missing
                if (section.line_items.some(li => (li.label || "").toLowerCase().includes("social media pack"))) {
                  setSocialMediaPack(true);
                }
              }
            }
            
            section.line_items?.forEach(item => {
              const meta = item.metadata || {};
              if (item.label.includes("Interior Modeling") && !item.label.includes("Amenity")) {
                setInteriorSqft(item.quantity || 0);
              } else if (item.label.includes("Amenity") && (item.unit_type === "amenity_sqft" || meta.sqft)) {
                // New sqft-based amenity restoration
                setAmenityEntries(prev => [...prev, {
                  id: `am-${Date.now()}-${prev.length}`,
                  name: item.label.replace(/^Amenity\s*:?\s*/i, "").replace(/\s*Interior.*$/i, "").trim() || "",
                  sqft: meta.sqft || 0,
                  complexity: meta.complexity || "standard",
                  modelingType: meta.modelingType || "interior_existing_structure",
                  structureSize: meta.structureSize || "medium",
                  sharedGroupId: meta.sharedGroupId || ""
                }]);
              } else if (item.label.includes("Interior Render")) {
                setInteriorRenders(item.quantity || 0);
              } else if (item.label.includes("Exterior Render")) {
                setExteriorRenders(item.quantity || 0);
              } else if (item.label.includes("Aerial")) {
                setAerialRenders(item.quantity || 0);
              } else if (item.label.includes("Floor Plan")) {
                setFloorPlans(item.quantity || 0);
              }
              // VR Tour restoration - Residential
              if (meta.category === "residential" || (item.label.includes("VR Tour") && !item.label.includes("Amenity"))) {
                const tourType = meta.tour_type || 
                  (item.label.includes("Studio") ? "studio" : 
                   item.label.includes("1 Bed") ? "1bed" :
                   item.label.includes("2 Bed") ? "2bed" :
                   item.label.includes("3") ? "3bed" : null);
                if (tourType && vrResidential[tourType] !== undefined) {
                  setVrResidential(prev => ({
                    ...prev,
                    [tourType]: {
                      quantity: item.quantity || 0,
                      additionalPoints: meta.additional_points || 0
                    }
                  }));
                }
              }
              // VR Tour restoration - Amenity
              if (meta.category === "amenity") {
                const tourType = meta.tour_type;
                if (tourType && vrAmenity[tourType] !== undefined) {
                  setVrAmenity(prev => ({
                    ...prev,
                    [tourType]: {
                      quantity: item.quantity || 0,
                      additionalPoints: meta.additional_points || 0
                    }
                  }));
                }
              }
            });
          });
        }
      } catch (err) {
        toast.error("Failed to load data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [quoteId, isNew]);

  function getRate(label, type = null) {
    const filtered = type ? pricingRules.filter(r => r.calculator_type === type) : pricingRules;
    const rule = filtered.find(r => r.label.toLowerCase().includes(label.toLowerCase()));
    return rule?.default_rate || DEFAULT_VR_RATES[label] || 0;
  }

  // Video Animation — Pricing-Admin first, hardcoded fallback. Same contract as CombinedCalculator.
  function getVideoAnimationRate(motionType, duration) {
    const rule = (pricingRules || []).find(r =>
      r.category === "video_animation" &&
      r.calculator_type === "visualization" &&
      r.tier_config?.motion_type === motionType &&
      Number(r.tier_config?.duration_seconds) === Number(duration)
    );
    if (rule && typeof rule.default_rate === "number") return rule.default_rate;
    return getAnimationPrice(motionType, duration);
  }

  // Get structure tier rate for exterior modeling - uses complexity category from pricing rules
  function getStructureRate(complexity) {
    // First try to find from the new complexity category pricing rules
    const complexityRules = pricingRules.filter(r => r.category === "complexity");
    if (complexityRules.length > 0) {
      const rule = complexityRules.find(r => 
        r.tier_config?.tier_value === complexity || 
        r.label.toLowerCase().includes(complexity.toLowerCase())
      );
      if (rule) return rule.default_rate;
    }
    
    // Fallback to old label-based lookup
    const rateMap = {
      small: getRate("structure - small", "vexos") || DEFAULT_EXT_RATES.small,
      medium: getRate("structure - medium", "vexos") || DEFAULT_EXT_RATES.medium,
      large: getRate("structure - large", "vexos") || DEFAULT_EXT_RATES.large,
      xl: getRate("structure - xl", "vexos") || DEFAULT_EXT_RATES.xl
    };
    return rateMap[complexity] || DEFAULT_EXT_RATES.medium;
  }

  // Get site environment rate - uses site_environment category from pricing rules
  function getSiteRate(env) {
    if (env === "none") return 0;
    
    // First try to find from the new site_environment category pricing rules
    const siteRules = pricingRules.filter(r => r.category === "site_environment");
    if (siteRules.length > 0) {
      const rule = siteRules.find(r => 
        r.tier_config?.tier_value === env || 
        r.label.toLowerCase() === env.toLowerCase()
      );
      if (rule) return rule.default_rate;
    }
    
    // Fallback to old label-based lookup
    const rateMap = {
      simple: getRate("site environment - simple", "vexos") || DEFAULT_EXT_RATES.site_simple,
      standard: getRate("site environment - standard", "vexos") || DEFAULT_EXT_RATES.site_standard,
      complex: getRate("site environment - complex", "vexos") || DEFAULT_EXT_RATES.site_complex,
      masterplan: getRate("site environment - masterplan", "vexos") || DEFAULT_EXT_RATES.site_masterplan
    };
    return rateMap[env] || 0;
  }

  // Get replication rate
  function getReplicationRate() {
    return getRate("replication rate", "vexos") || DEFAULT_EXT_RATES.replication;
  }

  // Update VR residential state
  function updateResidentialVR(key, field, value) {
    setVrResidential(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  }

  // Update VR amenity state
  function updateAmenityVR(key, field, value) {
    setVrAmenity(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  }

  // Update VR Residential Customizer
  function updateResidentialCustomizer(key, field, value) {
    setVrResidentialCustomizer(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  }
  
  // Update VR Amenity Customizer
  function updateAmenityCustomizer(key, field, value) {
    setVrAmenityCustomizer(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  }
  
  // Calculate customizer cost for a single tour type
  function calculateCustomizerCost(customizerData) {
    if (!customizerData.enabled || customizerData.categories.length === 0) {
      return { baseFee: 0, categoriesCost: 0, total: 0, categoryDetails: [] };
    }
    
    const baseFee = customizerPricing.baseFee;
    let categoriesCost = 0;
    const categoryDetails = [];
    
    customizerData.categories.forEach(cat => {
      let pricePerOption;
      let tierLabel;
      
      // Check if this is Furniture Package (premium pricing)
      if (cat.type === "furniture_package") {
        if (cat.optionCount === 1) {
          pricePerOption = customizerPricing.furnitureTier1;
          tierLabel = "1 option";
        } else if (cat.optionCount === 2) {
          pricePerOption = customizerPricing.furnitureTier2;
          tierLabel = "2 options";
        } else if (cat.optionCount === 3) {
          pricePerOption = customizerPricing.furnitureTier3;
          tierLabel = "3 options";
        } else {
          pricePerOption = customizerPricing.furnitureTier4Plus;
          tierLabel = `${cat.optionCount} options`;
        }
      } else {
        // Standard tiered option pricing
        if (cat.optionCount === 1) {
          pricePerOption = customizerPricing.optionPriceTier1;
          tierLabel = "1 option";
        } else if (cat.optionCount === 2) {
          pricePerOption = customizerPricing.optionPriceTier2;
          tierLabel = "2 options";
        } else {
          pricePerOption = customizerPricing.optionPriceTier3Plus;
          tierLabel = `${cat.optionCount} options`;
        }
      }
      
      const categoryTotal = cat.optionCount * pricePerOption;
      categoriesCost += categoryTotal;
      
      categoryDetails.push({
        type: cat.type,
        name: cat.type === "custom" ? cat.customName : CUSTOMIZER_CATEGORY_TYPES.find(t => t.value === cat.type)?.label || cat.type,
        optionCount: cat.optionCount,
        pricePerOption,
        tierLabel,
        lineTotal: categoryTotal,
        isFurniture: cat.type === "furniture_package"
      });
    });
    
    return {
      baseFee,
      categoriesCost,
      total: baseFee + categoriesCost,
      categoryDetails
    };
  }

  // Structure management functions
  function addStructure() {
    setExteriorStructures([...exteriorStructures, createEmptyStructure()]);
  }

  function updateStructure(id, field, value) {
    setExteriorStructures(exteriorStructures.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  }

  function removeStructure(id) {
    if (exteriorStructures.length > 1) {
      setExteriorStructures(exteriorStructures.filter(s => s.id !== id));
    }
  }

  // Calculate Exterior Modeling totals
  const exteriorCalc = useMemo(() => {
    const replicationRate = getReplicationRate();
    
    const structureDetails = exteriorStructures.map(s => {
      const tierRate = getStructureRate(s.complexity);
      const primaryCost = s.uniqueCount * tierRate;
      const replicationCost = s.repeatCount * replicationRate;
      const totalCost = primaryCost + replicationCost;
      
      return {
        ...s,
        tierRate,
        replicationRate,
        primaryCost,
        replicationCost,
        totalCost
      };
    });

    const totalUniqueStructures = structureDetails.reduce((sum, s) => sum + s.uniqueCount, 0);
    const totalReplicatedPlacements = structureDetails.reduce((sum, s) => sum + s.repeatCount, 0);
    const primaryStructureSubtotal = structureDetails.reduce((sum, s) => sum + s.primaryCost, 0);
    const replicationSubtotal = structureDetails.reduce((sum, s) => sum + s.replicationCost, 0);
    const siteEnvironmentCost = getSiteRate(siteEnvironment);
    const realityCaptureCost = includeRealityCapture ? realityCaptureRate : 0;
    
    const exteriorModelingTotal = primaryStructureSubtotal + replicationSubtotal + siteEnvironmentCost + realityCaptureCost;

    return {
      structureDetails,
      totalUniqueStructures,
      totalReplicatedPlacements,
      primaryStructureSubtotal,
      replicationSubtotal,
      siteEnvironmentCost,
      realityCaptureCost,
      exteriorModelingTotal
    };
  }, [exteriorStructures, siteEnvironment, includeRealityCapture, realityCaptureRate, pricingRules]);

  // Has Interior Visualization scope - determines if Interior Design Direction should show
  const hasInteriorVisualization = useMemo(() => {
    return interiorRenders > 0 || 
           Object.values(vrResidential).some(v => v.quantity > 0) ||
           Object.values(vrAmenity).some(v => v.quantity > 0) ||
           amenityEntries.length > 0 ||
           interiorSqft > 0;
  }, [interiorRenders, vrResidential, vrAmenity, amenityEntries, interiorSqft]);

  // Auto-detect interior spaces based on selected tours and renders
  const autoDetectedInteriorSpaces = useMemo(() => {
    // Count VR tours
    const residentialTourCount = Object.values(vrResidential).reduce((sum, v) => sum + v.quantity, 0);
    const amenityTourCount = Object.values(vrAmenity).reduce((sum, v) => sum + v.quantity, 0);
    
    // Use max of: interior renders, residential tours, or amenity tours
    return Math.max(interiorRenders, residentialTourCount, amenityTourCount);
  }, [interiorRenders, vrResidential, vrAmenity]);

  // Get the effective space count (override or auto-detected)
  const effectiveInteriorSpaceCount = useMemo(() => {
    if (interiorDesignSpacesOverride !== null && interiorDesignSpacesOverride >= 0) {
      return interiorDesignSpacesOverride;
    }
    return autoDetectedInteriorSpaces;
  }, [interiorDesignSpacesOverride, autoDetectedInteriorSpaces]);

  // Initialize/sync interior design spaces when count changes
  useEffect(() => {
    setInteriorDesignSpaces(prev => {
      const targetCount = effectiveInteriorSpaceCount;
      if (prev.length === targetCount) return prev;
      
      if (prev.length < targetCount) {
        // Add new spaces
        const newSpaces = [...prev];
        for (let i = prev.length; i < targetCount; i++) {
          newSpaces.push({
            id: crypto.randomUUID(),
            name: `Space ${i + 1}`,
            completeness_level: "full_package",
            notes: ""
          });
        }
        return newSpaces;
      } else {
        // Remove excess spaces
        return prev.slice(0, targetCount);
      }
    });
  }, [effectiveInteriorSpaceCount]);

  // Update interior design space
  function updateInteriorDesignSpace(id, field, value) {
    setInteriorDesignSpaces(prev => 
      prev.map(space => 
        space.id === id ? { ...space, [field]: value } : space
      )
    );
  }

  // Calculate interior design direction fee
  const interiorDesignDirectionCalc = useMemo(() => {
    if (!hasInteriorVisualization || interiorDesignSpaces.length === 0) {
      return { spaces: [], totalFee: 0 };
    }

    // Admin-managed override of Partial / Concept Creation fees from /pricing-rules.
    const designRules = (pricingRules || []).filter(
      r => r.category === "interior_design_direction" && r.active !== false
    );
    const ruleByLevel = {};
    designRules.forEach(r => {
      const levelKey = r?.tier_config?.completeness_level;
      if (levelKey) ruleByLevel[levelKey] = Number(r.default_rate) || 0;
    });

    const spacesWithFees = interiorDesignSpaces.map(space => {
      const levelConfig = DESIGN_COMPLETENESS_LEVELS.find(l => l.value === space.completeness_level);
      const adminFee = ruleByLevel[space.completeness_level];
      const fee = adminFee != null ? adminFee : (levelConfig?.fee || 0);
      return {
        ...space,
        fee,
        levelLabel: levelConfig?.shortLabel || "Unknown"
      };
    });

    const totalFee = spacesWithFees.reduce((sum, s) => sum + s.fee, 0);

    return { spaces: spacesWithFees, totalFee };
  }, [hasInteriorVisualization, interiorDesignSpaces, pricingRules]);

  // Calculate totals
  const calculations = useMemo(() => {
    // Tiered interior modeling — ONE rate based on combined (residential + amenity) SF, applied to both.
    const interiorModeling = calculateInteriorModeling({
      residentialSqft: interiorSqft,
      amenityEntries,
      tiers: interiorTiers
    });
    const interiorModelingRate = interiorModeling.appliedRate;
    const interiorModelingCost = interiorModeling.residentialModelingCost;
    const amenityDetails = interiorModeling.amenityDetails;
    const amenityModelingCost = interiorModeling.amenityModelingCost;
    const interiorModelingSubtotal = interiorModeling.interiorModelingSubtotal;
    const combinedInteriorSqft = interiorModeling.combinedInteriorSqft;
    const interiorModelingTierLabel = interiorModeling.tierLabel;

    // Rendering
    const interiorRenderRate = getRate("interior rendering") || 3500;
    const exteriorRenderRate = getRate("exterior rendering") || 4500;
    const interiorRenderCost = interiorRenders * interiorRenderRate;
    const exteriorRenderCost = exteriorRenders * exteriorRenderRate;
    const aerialRenderCost = aerialRenders * aerialRate;
    const floorPlanCost = floorPlans * floorPlanRate;
    const renderingSubtotal = interiorRenderCost + exteriorRenderCost + aerialRenderCost + floorPlanCost;

    // VR Tours - Residential (with Customizer)
    let residentialVRBaseCost = 0;
    let residentialVRAdditionalPointsCost = 0;
    let residentialCustomizerTotal = 0;
    let totalResidentialTours = 0;
    let totalResidentialAdditionalPoints = 0;

    const residentialVRDetails = RESIDENTIAL_VR_TOURS.map(config => {
      const data = vrResidential[config.key];
      const customizerData = vrResidentialCustomizer[config.key];
      const rate = getRate(config.rateKey) || DEFAULT_VR_RATES[config.rateKey];
      const baseCost = data.quantity * rate;
      const additionalCost = data.quantity * data.additionalPoints * additionalPointRate;
      
      // Calculate customizer cost for this tour type
      const customizerCalc = calculateCustomizerCost(customizerData);
      residentialCustomizerTotal += customizerCalc.total;
      
      const totalCost = baseCost + additionalCost + customizerCalc.total;
      
      residentialVRBaseCost += baseCost;
      residentialVRAdditionalPointsCost += additionalCost;
      totalResidentialTours += data.quantity;
      totalResidentialAdditionalPoints += data.quantity * data.additionalPoints;

      return {
        ...config,
        ...data,
        rate,
        baseCost,
        additionalCost,
        totalCost,
        finalPoints: config.includedPoints + data.additionalPoints,
        customizerEnabled: customizerData.enabled,
        customizerCalc
      };
    });

    const residentialVRSubtotal = residentialVRBaseCost + residentialVRAdditionalPointsCost + residentialCustomizerTotal;

    // VR Tours - Amenity (with Customizer)
    let amenityVRBaseCost = 0;
    let amenityVRAdditionalPointsCost = 0;
    let amenityCustomizerTotal = 0;
    let totalAmenityTours = 0;
    let totalAmenityAdditionalPoints = 0;

    const amenityVRDetails = AMENITY_VR_TOURS.map(config => {
      const data = vrAmenity[config.key];
      const customizerData = vrAmenityCustomizer[config.key];
      const rate = getRate(config.rateKey) || DEFAULT_VR_RATES[config.rateKey];
      const baseCost = data.quantity * rate;
      const additionalCost = data.quantity * data.additionalPoints * additionalPointRate;
      
      // Calculate customizer cost for this tour type
      const customizerCalc = calculateCustomizerCost(customizerData);
      amenityCustomizerTotal += customizerCalc.total;
      
      const totalCost = baseCost + additionalCost + customizerCalc.total;
      
      amenityVRBaseCost += baseCost;
      amenityVRAdditionalPointsCost += additionalCost;
      totalAmenityTours += data.quantity;
      totalAmenityAdditionalPoints += data.quantity * data.additionalPoints;

      return {
        ...config,
        ...data,
        rate,
        baseCost,
        additionalCost,
        totalCost,
        finalPoints: config.includedPoints + data.additionalPoints,
        customizerEnabled: customizerData.enabled,
        customizerCalc
      };
    });

    const amenityVRSubtotal = amenityVRBaseCost + amenityVRAdditionalPointsCost + amenityCustomizerTotal;
    const totalAdditionalPointsCost = residentialVRAdditionalPointsCost + amenityVRAdditionalPointsCost;
    const totalCustomizerCost = residentialCustomizerTotal + amenityCustomizerTotal;
    const vrSubtotal = residentialVRSubtotal + amenityVRSubtotal;

    // Animation - FIXED PRICING (no adjustments) — AI Motion or Cinematic Film + Social Media Pack add-on
    const animationBaseCost = animationEnabled ? getVideoAnimationRate(animationType, animationDuration) : 0;
    const socialMediaPackCost = animationEnabled && socialMediaPack ? SOCIAL_MEDIA_PACK.price : 0;
    const animationCost = animationBaseCost + socialMediaPackCost;
    const animationSubtotal = animationCost;
    const animationShots = animationEnabled ? getAnimationShots(animationType, animationDuration) : 0;

    // Interior Design Direction fee
    const interiorDesignFee = interiorDesignDirectionCalc.totalFee;

    // Grand Total now includes Exterior Modeling and Interior Design Direction
    const grandTotal = interiorModelingSubtotal + exteriorCalc.exteriorModelingTotal + renderingSubtotal + vrSubtotal + animationSubtotal + interiorDesignFee;

    return {
      interiorModelingRate,
      interiorModelingCost,
      amenityModelingCost,
      amenityDetails,
      interiorModelingSubtotal,
      combinedInteriorSqft,
      interiorModelingTierLabel,
      interiorRenderRate,
      exteriorRenderRate,
      interiorRenderCost,
      exteriorRenderCost,
      aerialRenderCost,
      floorPlanCost,
      renderingSubtotal,
      // VR Residential
      residentialVRDetails,
      residentialVRBaseCost,
      residentialVRAdditionalPointsCost,
      residentialVRSubtotal,
      totalResidentialTours,
      totalResidentialAdditionalPoints,
      residentialCustomizerTotal,
      // VR Amenity
      amenityVRDetails,
      amenityVRBaseCost,
      amenityVRAdditionalPointsCost,
      amenityVRSubtotal,
      totalAmenityTours,
      totalAmenityAdditionalPoints,
      amenityCustomizerTotal,
      // VR Combined
      vrSubtotal,
      totalAdditionalPointsCost,
      totalCustomizerCost,
      // Animation
      animationCost,
      animationBaseCost,
      socialMediaPackCost,
      animationSubtotal,
      animationShots,
      // Interior Design Direction
      interiorDesignFee,
      grandTotal
    };
  }, [pricingRules, interiorSqft, interiorTiers, amenityEntries, amenityBaseRate, amenityComplexityMultipliers, interiorRenders, exteriorRenders, 
      aerialRenders, aerialRate, floorPlans, floorPlanRate, vrResidential, vrAmenity, 
      additionalPointRate, animationEnabled, animationType, animationDuration, socialMediaPack, exteriorCalc.exteriorModelingTotal,
      vrResidentialCustomizer, vrAmenityCustomizer, customizerPricing, interiorDesignDirectionCalc]);

  // Payment Plan milestones — Visualization scope only
  const visualizationScopeTotal = calculations.grandTotal;
  const activeMilestones = useMemo(() => {
    return PAYMENT_PRESETS[paymentPreset]?.milestones || PAYMENT_PRESETS["50-50"].milestones;
  }, [paymentPreset]);
  const vizMilestoneAmounts = useMemo(() => {
    return activeMilestones.map(m => ({
      ...m,
      amount: (visualizationScopeTotal * m.percent) / 100
    }));
  }, [activeMilestones, visualizationScopeTotal]);

  // Build deliverables for timeline estimation
  const timelineDeliverables = useMemo(() => {
    const items = [];
    // Exterior Modeling
    if (exteriorCalc.exteriorModelingTotal > 0) {
      items.push({ deliverable_type: "High Rise Model", quantity: exteriorCalc.totalUniqueStructures || 1 });
    }
    // Interior Modeling
    if (calculations.interiorModelingCost > 0) {
      const modelUnits = Math.max(1, Math.ceil(interiorSqft / 1000));
      items.push({ deliverable_type: "Interior Unit S/M", quantity: modelUnits });
    }
    if (calculations.amenityModelingCost > 0) {
      items.push({ deliverable_type: "Interior Amenities", quantity: amenityEntries.length || 1 });
    }
    // Renderings
    if (interiorRenders > 0) items.push({ deliverable_type: "Interior Render", quantity: interiorRenders });
    if (exteriorRenders > 0) items.push({ deliverable_type: "Exterior Render", quantity: exteriorRenders });
    if (aerialRenders > 0) items.push({ deliverable_type: "Exterior Hero Day", quantity: aerialRenders });
    if (floorPlans > 0) items.push({ deliverable_type: "Floor Plans", quantity: floorPlans });
    // VR / 360 Tours
    if (calculations.totalResidentialTours > 0) items.push({ deliverable_type: "Interior 360", quantity: calculations.totalResidentialTours });
    if (calculations.totalAmenityTours > 0) items.push({ deliverable_type: "Interior 360 Amenities", quantity: calculations.totalAmenityTours });
    // Animation
    if (animationEnabled) {
      const animLabel = animationType === "ai"
        ? `AI Motion ${animationDuration}sec`
        : `Animation ${animationDuration}sec`;
      items.push({ deliverable_type: animLabel, quantity: 1 });
      if (socialMediaPack) {
        items.push({ deliverable_type: "Social Media Pack", quantity: 1 });
      }
    }
    return items;
  }, [exteriorCalc, calculations, interiorSqft, amenityEntries, interiorRenders, exteriorRenders, aerialRenders, floorPlans, animationEnabled, animationType, animationDuration, socialMediaPack]);

  // Build sections for saving
  function buildSections() {
    const replicationRate = getReplicationRate();
    const sections = [
      {
        id: crypto.randomUUID(),
        section_type: "viz_interior_modeling",
        label: "Interior Modeling",
        line_items: [
          ...(calculations.interiorModelingCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Interior Modeling",
            quantity: interiorSqft,
            rate: calculations.interiorModelingRate,
            subtotal: calculations.interiorModelingCost,
            unit_type: "sqft"
          }] : []),
          ...(calculations.amenityModelingCost > 0 ? calculations.amenityDetails.map(a => ({
            id: crypto.randomUUID(),
            label: `${a.name || "Amenity"} Interior${a.structureFee > 0 ? " + Structure" : ""}`,
            quantity: 1,
            rate: a.totalCost,
            subtotal: a.totalCost,
            unit_type: "amenity_sqft",
            metadata: {
              sqft: a.sqft,
              complexity: a.complexity,
              modelingType: a.modelingType,
              structureSize: a.structureSize,
              sharedGroupId: a.sharedGroupId
            }
          })) : [])
        ],
        subtotal: calculations.interiorModelingSubtotal
      },
      // Exterior Modeling Section (NEW)
      {
        id: crypto.randomUUID(),
        section_type: "viz_exterior_modeling",
        label: "Exterior Modeling",
        line_items: [
          // Structure line items
          ...exteriorCalc.structureDetails.filter(s => s.uniqueCount > 0 || s.repeatCount > 0).map(s => ({
            id: s.id,
            label: s.name || `${complexityTiers.find(t => t.value === s.complexity)?.label || 'Medium'} Structure`,
            quantity: s.uniqueCount,
            rate: s.tierRate,
            subtotal: s.totalCost,
            unit_type: "structure",
            metadata: {
              item_type: "structure",
              structure_name: s.name,
              complexity_tier: s.complexity,
              tier_rate: s.tierRate,
              unique_count: s.uniqueCount,
              repeat_count: s.repeatCount,
              replication_rate: replicationRate,
              primary_cost: s.primaryCost,
              replication_cost: s.replicationCost,
              notes: s.notes
            }
          })),
          // Site environment line item
          ...(exteriorCalc.siteEnvironmentCost > 0 ? [{
            id: crypto.randomUUID(),
            label: `Site Environment — ${siteEnvironments.find(e => e.value === siteEnvironment)?.label || 'Standard'}`,
            quantity: 1,
            rate: exteriorCalc.siteEnvironmentCost,
            subtotal: exteriorCalc.siteEnvironmentCost,
            unit_type: "site",
            metadata: {
              item_type: "site_environment",
              environment_type: siteEnvironment
            }
          }] : []),
          // Reality capture line item
          ...(exteriorCalc.realityCaptureCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Reality Capture & Environment Reconstruction",
            description: "Drone capture, photogrammetry, terrain reconstruction",
            quantity: 1,
            rate: realityCaptureRate,
            subtotal: realityCaptureRate,
            metadata: {
              item_type: "reality_capture"
            }
          }] : [])
        ],
        subtotal: exteriorCalc.exteriorModelingTotal,
        metadata: {
          total_unique_structures: exteriorCalc.totalUniqueStructures,
          total_replicated_placements: exteriorCalc.totalReplicatedPlacements,
          primary_structure_subtotal: exteriorCalc.primaryStructureSubtotal,
          replication_subtotal: exteriorCalc.replicationSubtotal,
          site_environment: siteEnvironment,
          site_environment_cost: exteriorCalc.siteEnvironmentCost,
          reality_capture_enabled: includeRealityCapture,
          reality_capture_rate: realityCaptureRate
        }
      },
      {
        id: crypto.randomUUID(),
        section_type: "viz_rendering",
        label: "Renderings",
        line_items: [
          ...(calculations.interiorRenderCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Interior Renderings",
            quantity: interiorRenders,
            rate: calculations.interiorRenderRate,
            subtotal: calculations.interiorRenderCost,
            unit_type: "image"
          }] : []),
          ...(calculations.exteriorRenderCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Exterior Renderings",
            quantity: exteriorRenders,
            rate: calculations.exteriorRenderRate,
            subtotal: calculations.exteriorRenderCost,
            unit_type: "image"
          }] : []),
          ...(calculations.aerialRenderCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Aerial Renderings",
            quantity: aerialRenders,
            rate: aerialRate,
            subtotal: calculations.aerialRenderCost,
            unit_type: "image"
          }] : []),
          ...(calculations.floorPlanCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "3D Floor Plans",
            quantity: floorPlans,
            rate: floorPlanRate,
            subtotal: calculations.floorPlanCost,
            unit_type: "plan"
          }] : [])
        ],
        subtotal: calculations.renderingSubtotal
      },
      // VR Tours - Residential
      {
        id: crypto.randomUUID(),
        section_type: "viz_vr_residential",
        label: "Residential VR Tours",
        line_items: calculations.residentialVRDetails
          .filter(d => d.quantity > 0)
          .map(d => ({
            id: crypto.randomUUID(),
            label: `VR Tour — ${d.label}`,
            quantity: d.quantity,
            rate: d.rate,
            subtotal: d.totalCost,
            unit_type: "tour",
            metadata: {
              category: "residential",
              tour_type: d.key,
              included_points: d.includedPoints,
              additional_points: d.additionalPoints,
              final_points: d.finalPoints,
              base_rate: d.rate,
              additional_point_rate: additionalPointRate,
              base_cost: d.baseCost,
              additional_cost: d.additionalCost
            }
          })),
        subtotal: calculations.residentialVRSubtotal,
        metadata: {
          total_tours: calculations.totalResidentialTours,
          total_additional_points: calculations.totalResidentialAdditionalPoints
        }
      },
      // VR Tours - Amenity
      {
        id: crypto.randomUUID(),
        section_type: "viz_vr_amenity",
        label: "Amenity VR Tours",
        line_items: calculations.amenityVRDetails
          .filter(d => d.quantity > 0)
          .map(d => ({
            id: crypto.randomUUID(),
            label: `Amenity VR Tour — ${d.label}`,
            quantity: d.quantity,
            rate: d.rate,
            subtotal: d.totalCost,
            unit_type: "tour",
            metadata: {
              category: "amenity",
              tour_type: d.key,
              included_points: d.includedPoints,
              additional_points: d.additionalPoints,
              final_points: d.finalPoints,
              base_rate: d.rate,
              additional_point_rate: additionalPointRate,
              base_cost: d.baseCost,
              additional_cost: d.additionalCost
            }
          })),
        subtotal: calculations.amenityVRSubtotal,
        metadata: {
          total_tours: calculations.totalAmenityTours,
          total_additional_points: calculations.totalAmenityAdditionalPoints
        }
      }
    ];

    if (calculations.animationSubtotal > 0) {
      const lineItems = [{
        id: crypto.randomUUID(),
        label: getAnimationDisplayName(animationType, animationDuration),
        quantity: 1,
        rate: getVideoAnimationRate(animationType, animationDuration),
        subtotal: calculations.animationBaseCost,
        unit_type: "package",
        metadata: {
          animation_type: animationType,
          duration_seconds: animationDuration,
          shot_count: getAnimationShots(animationType, animationDuration)
        }
      }];
      if (socialMediaPack) {
        lineItems.push({
          id: crypto.randomUUID(),
          label: SOCIAL_MEDIA_PACK.label,
          quantity: 1,
          rate: SOCIAL_MEDIA_PACK.price,
          subtotal: SOCIAL_MEDIA_PACK.price,
          unit_type: "package",
          metadata: { addon: "social_media_pack" }
        });
      }
      sections.push({
        id: crypto.randomUUID(),
        section_type: "viz_animation",
        label: "Video Animation",
        line_items: lineItems,
        subtotal: calculations.animationSubtotal,
        metadata: {
          animation_type: animationType,
          duration_seconds: animationDuration,
          shot_count: getAnimationShots(animationType, animationDuration),
          modeling_aligned: animationModelingAligned,
          social_media_pack: socialMediaPack,
          includes: getAnimationIncludes(animationType)
        }
      });
    }

    // Interior Design Direction section
    if (interiorDesignDirectionCalc.totalFee > 0) {
      sections.push({
        id: crypto.randomUUID(),
        section_type: "viz_interior_design_direction",
        label: "Interior Design Direction",
        line_items: interiorDesignDirectionCalc.spaces
          .filter(s => s.fee > 0)
          .map(s => ({
            id: crypto.randomUUID(),
            label: `${s.name} — ${s.levelLabel}`,
            quantity: 1,
            rate: s.fee,
            subtotal: s.fee,
            unit_type: "space"
          })),
        subtotal: interiorDesignDirectionCalc.totalFee,
        metadata: {
          total_spaces: interiorDesignSpaces.length,
          auto_detected: autoDetectedInteriorSpaces,
          override_applied: interiorDesignSpacesOverride !== null
        }
      });
    }

    return sections;
  }

  async function handleSave() {
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    if (!clientName.trim()) {
      toast.error("Please enter a client name");
      return;
    }
    if (!salesperson) {
      toast.error("Please select a salesperson");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        project_name: projectName,
        client_name: clientName,
        salesperson,
        quote_type: "visualization",
        sections: buildSections(),
        payment_plan: {
          preset: paymentPreset,
          milestones: activeMilestones,
          visualization_scope_total: visualizationScopeTotal,
          one_time_total: calculations.grandTotal
        },
        notes
      };

      if (isNew) {
        const res = await apiClient.post("/calculators/quotes", payload);
        toast.success("Quote created");
        navigate(`/sales/calculators/visualization/${res.data.id}`, { replace: true });
      } else {
        await apiClient.put(`/calculators/quotes/${quoteId}`, payload);
        toast.success("Quote saved");
      }
    } catch (err) {
      toast.error("Failed to save quote");
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function toggleSection(section) {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }

  async function handleGenerateProposal() {
    if (!quoteId || isNew) {
      toast.error("Please save the quote first");
      return;
    }

    setGeneratingProposal(true);
    try {
      const res = await apiClient.post(`/calculators/quotes/${quoteId}/generate-proposal`, {
        format: "html"
      });
      setProposalHtml(res.data.html);
      toast.success("Proposal generated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate proposal");
    } finally {
      setGeneratingProposal(false);
    }
  }

  function handlePrintProposal() {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(proposalHtml);
    printWindow.document.close();
    printWindow.print();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/sales/calculators")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-500" />
                <h1 className="text-xl font-light">Visualization Quote</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                {isNew ? "New Quote" : projectName || "Untitled"}
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Panel - Inputs */}
          <div className="col-span-2 space-y-6">
            {/* Project Info */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Project</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project Name</Label>
                    <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g., One River Point" data-testid="project-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="e.g., Related Group" data-testid="client-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Salesperson</Label>
                  <Select value={salesperson} onValueChange={setSalesperson}>
                    <SelectTrigger className="w-1/2" data-testid="salesperson-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {SALESPEOPLE.map(name => (<SelectItem key={name} value={name}>{name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Interior Modeling */}
            <Collapsible open={expandedSections.interiorModeling} onOpenChange={() => toggleSection("interiorModeling")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Box className="w-5 h-5 text-blue-500" />
                        Interior Modeling
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-blue-500">${calculations.interiorModelingSubtotal.toLocaleString()}</span>
                        {expandedSections.interiorModeling ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="flex items-center justify-between -mt-1">
                      <p className="text-[11px] text-muted-foreground">Interior Modeling Rate (based on total combined interior modeled SF). Applies to both residential and amenity interiors.</p>
                      <Badge variant="outline" className="text-[10px] shrink-0 ml-2" data-testid="interior-modeling-tier-badge">{calculations.combinedInteriorSqft.toLocaleString()} SF · ${calculations.interiorModelingRate.toFixed(2)}/SF</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Interior (sqft)</Label>
                        <Input type="number" value={interiorSqft} onChange={(e) => setInteriorSqft(parseInt(e.target.value) || 0)} data-testid="interior-sqft-input" />
                        <p className="text-xs text-muted-foreground">${calculations.interiorModelingRate.toFixed(2)}/sqft (tiered)</p>
                      </div>
                      <div className="space-y-2 flex flex-col justify-end">
                        <p className="text-xs text-muted-foreground">Residential interior space total</p>
                      </div>
                    </div>

                    <Separator />

                    {/* Amenity Interiors — new sqft-based model */}
                    <div className="pt-1">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                          Amenity Interiors
                          {calculations.amenityModelingCost > 0 && <Badge variant="outline" className="ml-1 text-xs">${calculations.amenityModelingCost.toLocaleString()}</Badge>}
                        </Label>
                        <Button variant="outline" size="sm" onClick={addAmenity} className="h-7 gap-1 text-xs" data-testid="add-amenity-btn">
                          <Plus className="w-3 h-3" /> Add Amenity
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">${calculations.interiorModelingRate.toFixed(2)}/sqft (tiered — same rate as residential interior)</p>
                      {amenityEntries.length === 0 && (
                        <div className="text-center py-4 text-xs text-muted-foreground/60 border border-dashed rounded-lg">No amenity spaces added</div>
                      )}
                      <div className="space-y-2">
                        {amenityEntries.map((amenity) => {
                          const detail = calculations.amenityDetails?.find(d => d.id === amenity.id);
                          return (
                            <div key={amenity.id} className={`p-3 rounded-lg border transition-all ${detail?.totalCost > 0 ? 'border-blue-500/30 bg-blue-500/5' : 'border-border'}`} data-testid={`amenity-entry-${amenity.id}`}>
                              <div className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-3">
                                  <Input value={amenity.name} onChange={(e) => updateAmenity(amenity.id, "name", e.target.value)} placeholder="Amenity name" className="h-8 text-sm" />
                                </div>
                                <div className="col-span-2">
                                  <Input type="number" value={amenity.sqft} onChange={(e) => updateAmenity(amenity.id, "sqft", parseInt(e.target.value) || 0)} placeholder="sqft" className="h-8 text-sm" data-testid={`amenity-sqft-${amenity.id}`} />
                                </div>
                                <div className="col-span-2">
                                  <Select value={amenity.complexity} onValueChange={(v) => updateAmenity(amenity.id, "complexity", v)}>
                                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="standard">Standard</SelectItem>
                                      <SelectItem value="premium">Premium</SelectItem>
                                      <SelectItem value="signature">Signature</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2">
                                  <Select value={amenity.modelingType} onValueChange={(v) => updateAmenity(amenity.id, "modelingType", v)}>
                                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="interior_existing_structure">Interior Only</SelectItem>
                                      <SelectItem value="standalone_structure">Standalone</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2 flex items-center gap-1">
                                  <span className="text-sm font-medium text-blue-500 flex-1 text-right">${(detail?.totalCost || 0).toLocaleString()}</span>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeAmenity(amenity.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              {amenity.modelingType === "standalone_structure" && (
                                <div className="grid grid-cols-12 gap-2 mt-2 items-center">
                                  <div className="col-span-3">
                                    <Select value={amenity.structureSize} onValueChange={(v) => updateAmenity(amenity.id, "structureSize", v)}>
                                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Structure size" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="small">Small ($1,000)</SelectItem>
                                        <SelectItem value="medium">Medium ($2,000)</SelectItem>
                                        <SelectItem value="large">Large ($3,500)</SelectItem>
                                        <SelectItem value="xl">XL ($5,000)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-4">
                                    <Input value={amenity.sharedGroupId} onChange={(e) => updateAmenity(amenity.id, "sharedGroupId", e.target.value)} placeholder="Shared group (optional)" className="h-7 text-xs" />
                                  </div>
                                  <div className="col-span-5">
                                    {detail?.structureCharged && <span className="text-xs text-emerald-600">+${detail.structureFee.toLocaleString()} structure fee</span>}
                                    {!detail?.structureCharged && amenity.sharedGroupId && <span className="text-xs text-muted-foreground">Structure shared</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Exterior Modeling - NEW SECTION */}
            <Collapsible open={expandedSections.exteriorModeling} onOpenChange={() => toggleSection("exteriorModeling")}>
              <Card className="border-2 border-emerald-500/20">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mountain className="w-5 h-5 text-emerald-500" />
                        Exterior Modeling
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-emerald-500">
                          ${exteriorCalc.exteriorModelingTotal.toLocaleString()}
                        </span>
                        {expandedSections.exteriorModeling ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Structure Rows */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <h4 className="font-semibold">Primary Structures</h4>
                        </div>
                        <Button variant="outline" size="sm" onClick={addStructure} className="gap-1">
                          <Plus className="w-3 h-3" /> Add Structure
                        </Button>
                      </div>

                      {exteriorStructures.map((structure, index) => {
                        const details = exteriorCalc.structureDetails.find(d => d.id === structure.id);
                        return (
                          <div 
                            key={structure.id} 
                            className={`p-4 rounded-xl border-2 transition-all ${
                              (structure.uniqueCount > 0 || structure.repeatCount > 0) 
                                ? 'border-emerald-500/30 bg-emerald-500/5' 
                                : 'border-border'
                            }`}
                            data-testid={`structure-row-${index}`}
                          >
                            <div className="grid grid-cols-12 gap-4 items-start">
                              {/* Structure Name */}
                              <div className="col-span-4 space-y-1">
                                <Label className="text-xs text-muted-foreground">Structure Name</Label>
                                <Input
                                  value={structure.name}
                                  onChange={(e) => updateStructure(structure.id, "name", e.target.value)}
                                  placeholder="e.g., Tower A, Villa Model B"
                                  className="text-sm"
                                />
                              </div>

                              {/* Complexity Tier */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground">Complexity</Label>
                                <Select 
                                  value={structure.complexity} 
                                  onValueChange={(v) => updateStructure(structure.id, "complexity", v)}
                                >
                                  <SelectTrigger className="text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {complexityTiers.map(tier => (
                                      <SelectItem key={tier.value} value={tier.value}>
                                        <div className="flex flex-col">
                                          <span>{tier.label}</span>
                                          <span className="text-xs text-muted-foreground">{tier.description}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                  ${getStructureRate(structure.complexity).toLocaleString()}/ea
                                </p>
                              </div>

                              {/* Unique Count */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground">Unique</Label>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    value={structure.uniqueCount}
                                    onChange={(e) => updateStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))}
                                    className="text-center text-sm h-8"
                                  />
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>

                              {/* Repeat Count */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Repeat className="w-3 h-3" /> Repeated
                                </Label>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    value={structure.repeatCount}
                                    onChange={(e) => updateStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))}
                                    className="text-center text-sm h-8"
                                  />
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "repeatCount", structure.repeatCount + 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                                {structure.repeatCount > 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    ${getReplicationRate()}/placement
                                  </p>
                                )}
                              </div>

                              {/* Row Total & Delete */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground">Row Total</Label>
                                <div className="flex items-center gap-2">
                                  <div className="h-8 flex-1 px-3 rounded-md bg-muted/50 flex items-center justify-end">
                                    <span className="font-semibold text-sm text-emerald-600">
                                      ${(details?.totalCost || 0).toLocaleString()}
                                    </span>
                                  </div>
                                  {exteriorStructures.length > 1 && (
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      onClick={() => removeStructure(structure.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Cost Breakdown for row */}
                            {(structure.uniqueCount > 0 || structure.repeatCount > 0) && (
                              <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
                                {details?.primaryCost > 0 && (
                                  <span>Primary: ${details.primaryCost.toLocaleString()}</span>
                                )}
                                {details?.replicationCost > 0 && (
                                  <span>Replication: ${details.replicationCost.toLocaleString()}</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Site Environment */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Trees className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-semibold">Site Environment</h4>
                      </div>
                      <Select value={siteEnvironment} onValueChange={setSiteEnvironment}>
                        <SelectTrigger data-testid="site-environment-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {siteEnvironments.map(env => (
                            <SelectItem key={env.value} value={env.value}>
                              <div className="flex items-center justify-between gap-4 w-full">
                                <div>
                                  <span className="font-medium">{env.label}</span>
                                  <p className="text-xs text-muted-foreground">{env.description}</p>
                                </div>
                                {env.value !== "none" && (
                                  <span className="text-xs font-medium">
                                    ${getSiteRate(env.value).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Reality Capture */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">Reality Capture & Environment Reconstruction</h4>
                          <p className="text-sm text-muted-foreground">Drone capture, photogrammetry, terrain reconstruction</p>
                        </div>
                        <div className="flex items-center gap-4">
                          {includeRealityCapture && (
                            <Input
                              type="number"
                              value={realityCaptureRate}
                              onChange={(e) => setRealityCaptureRate(parseInt(e.target.value) || 8500)}
                              className="w-28"
                              data-testid="reality-capture-rate-input"
                            />
                          )}
                          <Switch
                            checked={includeRealityCapture}
                            onCheckedChange={setIncludeRealityCapture}
                            data-testid="reality-capture-toggle"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Exterior Modeling Summary */}
                    <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 space-y-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Exterior Modeling Summary
                      </h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Unique Structures</p>
                          <p className="font-medium">{exteriorCalc.totalUniqueStructures}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Replicated</p>
                          <p className="font-medium">{exteriorCalc.totalReplicatedPlacements}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Site Complexity</p>
                          <p className="font-medium">{siteEnvironments.find(e => e.value === siteEnvironment)?.label || 'None'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Reality Capture</p>
                          <p className="font-medium">{includeRealityCapture ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                      <div className="pt-3 border-t flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Exterior Modeling Total</span>
                        <span className="text-lg font-bold text-emerald-600">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Renderings */}
            <Collapsible open={expandedSections.rendering} onOpenChange={() => toggleSection("rendering")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Image className="w-5 h-5 text-amber-500" />
                        Renderings
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-amber-500">${calculations.renderingSubtotal.toLocaleString()}</span>
                        {expandedSections.rendering ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Interior Renderings</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setInteriorRenders(Math.max(0, interiorRenders - 1))}><Minus className="w-4 h-4" /></Button>
                          <Input type="number" value={interiorRenders} onChange={(e) => setInteriorRenders(Math.max(0, parseInt(e.target.value) || 0))} className="text-center" />
                          <Button variant="outline" size="icon" onClick={() => setInteriorRenders(interiorRenders + 1)}><Plus className="w-4 h-4" /></Button>
                        </div>
                        <p className="text-xs text-muted-foreground">${calculations.interiorRenderRate.toLocaleString()} per image</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Exterior Renderings</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setExteriorRenders(Math.max(0, exteriorRenders - 1))}><Minus className="w-4 h-4" /></Button>
                          <Input type="number" value={exteriorRenders} onChange={(e) => setExteriorRenders(Math.max(0, parseInt(e.target.value) || 0))} className="text-center" />
                          <Button variant="outline" size="icon" onClick={() => setExteriorRenders(exteriorRenders + 1)}><Plus className="w-4 h-4" /></Button>
                        </div>
                        <p className="text-xs text-muted-foreground">${calculations.exteriorRenderRate.toLocaleString()} per image</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Aerial Renderings</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setAerialRenders(Math.max(0, aerialRenders - 1))}><Minus className="w-4 h-4" /></Button>
                          <Input type="number" value={aerialRenders} onChange={(e) => setAerialRenders(Math.max(0, parseInt(e.target.value) || 0))} className="text-center w-20" />
                          <Button variant="outline" size="icon" onClick={() => setAerialRenders(aerialRenders + 1)}><Plus className="w-4 h-4" /></Button>
                        </div>
                        <p className="text-xs text-muted-foreground">${aerialRate.toLocaleString()} per image</p>
                      </div>
                      <div className="space-y-2">
                        <Label>3D Floor Plans</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setFloorPlans(Math.max(0, floorPlans - 1))}><Minus className="w-4 h-4" /></Button>
                          <Input type="number" value={floorPlans} onChange={(e) => setFloorPlans(Math.max(0, parseInt(e.target.value) || 0))} className="text-center w-20" />
                          <Button variant="outline" size="icon" onClick={() => setFloorPlans(floorPlans + 1)}><Plus className="w-4 h-4" /></Button>
                        </div>
                        <p className="text-xs text-muted-foreground">${floorPlanRate.toLocaleString()} per plan</p>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* VR / 360° Tours */}
            <Collapsible open={expandedSections.vr} onOpenChange={() => toggleSection("vr")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Eye className="w-5 h-5 text-emerald-500" />
                        VR / 360° Tours
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-emerald-500">${calculations.vrSubtotal.toLocaleString()}</span>
                        {expandedSections.vr ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Residential VR Tours */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-muted-foreground" />
                        <span className="font-semibold">Residential Tours</span>
                        {calculations.residentialVRSubtotal > 0 && (
                          <Badge variant="outline" className="ml-auto">
                            ${calculations.residentialVRSubtotal.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {RESIDENTIAL_VR_TOURS.map(config => {
                          const data = vrResidential[config.key];
                          const customizerData = vrResidentialCustomizer[config.key];
                          const details = calculations.residentialVRDetails.find(d => d.key === config.key);
                          return (
                            <VRTourCard
                              key={config.key}
                              config={config}
                              quantity={data.quantity}
                              setQuantity={(v) => updateResidentialVR(config.key, "quantity", v)}
                              additionalPoints={data.additionalPoints}
                              setAdditionalPoints={(v) => updateResidentialVR(config.key, "additionalPoints", v)}
                              rate={details?.rate || 0}
                              totalCost={details?.baseCost + details?.additionalCost || 0}
                              customizerEnabled={customizerData.enabled}
                              setCustomizerEnabled={(v) => updateResidentialCustomizer(config.key, "enabled", v)}
                              customizerCategories={customizerData.categories}
                              setCustomizerCategories={(v) => updateResidentialCustomizer(config.key, "categories", v)}
                              customizerPricing={customizerPricing}
                              customizerCalc={details?.customizerCalc || { total: 0, categoryDetails: [] }}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* Amenity VR Tours */}
                    <div className="space-y-3 pt-3 border-t">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="font-semibold">Amenity Tours</span>
                        {calculations.amenityVRSubtotal > 0 && (
                          <Badge variant="outline" className="ml-auto">
                            ${calculations.amenityVRSubtotal.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {AMENITY_VR_TOURS.map(config => {
                          const data = vrAmenity[config.key];
                          const customizerData = vrAmenityCustomizer[config.key];
                          const details = calculations.amenityVRDetails.find(d => d.key === config.key);
                          return (
                            <VRTourCard
                              key={config.key}
                              config={config}
                              quantity={data.quantity}
                              setQuantity={(v) => updateAmenityVR(config.key, "quantity", v)}
                              additionalPoints={data.additionalPoints}
                              setAdditionalPoints={(v) => updateAmenityVR(config.key, "additionalPoints", v)}
                              rate={details?.rate || 0}
                              totalCost={details?.baseCost + details?.additionalCost || 0}
                              customizerEnabled={customizerData.enabled}
                              setCustomizerEnabled={(v) => updateAmenityCustomizer(config.key, "enabled", v)}
                              customizerCategories={customizerData.categories}
                              setCustomizerCategories={(v) => updateAmenityCustomizer(config.key, "categories", v)}
                              customizerPricing={customizerPricing}
                              customizerCalc={details?.customizerCalc || { total: 0, categoryDetails: [] }}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* Additional Points Info */}
                    {calculations.totalAdditionalPointsCost > 0 && (
                      <div className="p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Additional navigation points ({calculations.totalResidentialAdditionalPoints + calculations.totalAmenityAdditionalPoints} × ${additionalPointRate})
                          </span>
                        </div>
                        <span className="font-medium">${calculations.totalAdditionalPointsCost.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {/* Real-Time Customizer Total */}
                    {calculations.totalCustomizerCost > 0 && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-amber-600">
                            Real-Time Customizer Total
                          </span>
                        </div>
                        <span className="font-semibold text-amber-600">${calculations.totalCustomizerCost.toLocaleString()}</span>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Video Animation - AI Motion + Cinematic Film */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Video className="w-5 h-5 text-rose-500" />
                    Video Animation
                  </CardTitle>
                  {animationEnabled && calculations.animationSubtotal > 0 && (
                    <span className="text-lg font-semibold text-rose-500" data-testid="animation-subtotal">
                      ${calculations.animationSubtotal.toLocaleString()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a motion product. One selection at a time.
                </p>
              </CardHeader>

              <CardContent className="space-y-6 pt-0">
                <VideoAnimationPicker
                  selectedType={animationEnabled ? animationType : null}
                  selectedDuration={animationEnabled ? animationDuration : null}
                  socialMediaPack={socialMediaPack}
                  rateResolver={getVideoAnimationRate}
                  onSelect={(type, duration) => {
                    if (!type) {
                      setAnimationEnabled(false);
                      setAnimationModelingAligned(null);
                      setSocialMediaPack(false);
                    } else {
                      setAnimationEnabled(true);
                      setAnimationType(type);
                      setAnimationDuration(duration);
                    }
                  }}
                  onToggleSocialPack={setSocialMediaPack}
                  testIdPrefix="viz-animation"
                />

                {/* Modeling Alignment Validation — kept minimal, only when a video is selected */}
                {animationEnabled && (
                  <div className="space-y-3 pt-4 border-t border-white/[0.06]">
                    <Label className="text-sm font-medium">Modeling Alignment</Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Are all spaces in this video already accounted for in the modeling scope?
                    </p>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setAnimationModelingAligned(true)}
                        className={`px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
                          animationModelingAligned === true
                            ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                        data-testid="modeling-aligned-yes"
                      >
                        <div className="flex items-center gap-2">
                          {animationModelingAligned === true ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                          )}
                          <span className={`font-medium ${animationModelingAligned === true ? "text-emerald-700" : "text-foreground"}`}>
                            Yes — all spaces are modeled
                          </span>
                        </div>
                      </button>

                      <button
                        onClick={() => setAnimationModelingAligned(false)}
                        className={`px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
                          animationModelingAligned === false
                            ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                        data-testid="modeling-aligned-no"
                      >
                        <div className="flex items-center gap-2">
                          {animationModelingAligned === false ? (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                          )}
                          <span className={`font-medium ${animationModelingAligned === false ? "text-amber-700" : "text-foreground"}`}>
                            Not yet — needs update
                          </span>
                        </div>
                      </button>
                    </div>

                    {animationModelingAligned === false && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-amber-800">
                              Modeling scope needs review
                            </p>
                            <p className="text-sm text-amber-700">
                              Video animation requires all spaces to be fully modeled. Please update the modeling scope before proceeding.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
                          onClick={() => {
                            const modelingSection = document.querySelector('[data-section="interior-modeling"]');
                            if (modelingSection) {
                              modelingSection.scrollIntoView({ behavior: "smooth", block: "start" });
                              setExpandedSections(prev => ({ ...prev, interiorModeling: true }));
                            }
                          }}
                        >
                          Review Modeling Scope
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Interior Design Direction - Only shows when there's interior visualization scope */}
            {hasInteriorVisualization && (
              <Card className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Paintbrush className="w-5 h-5 text-violet-500" />
                      Interior Design Direction
                    </CardTitle>
                    {interiorDesignDirectionCalc.totalFee > 0 && (
                      <span className="text-lg font-semibold text-violet-600">
                        +${interiorDesignDirectionCalc.totalFee.toLocaleString()}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        This section helps determine whether TRP is replicating an existing interior design or creating design direction conceptually. Additional fees apply when interior design information is incomplete.
                      </p>
                    </div>
                  </div>
                  
                  {/* Space Count */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Spaces Requiring Interior Design Review</Label>
                      <span className="text-lg font-semibold text-foreground">{effectiveInteriorSpaceCount}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Based on interior renders and tours currently selected
                    </p>
                    
                    {/* Manual Override */}
                    <div className="flex items-center gap-2 pt-2">
                      <Label className="text-xs text-muted-foreground">Manual Override:</Label>
                      <Input 
                        type="number" 
                        min="0"
                        className="w-20 h-8 text-sm"
                        value={interiorDesignSpacesOverride ?? ""}
                        placeholder={String(autoDetectedInteriorSpaces)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || val === null) {
                            setInteriorDesignSpacesOverride(null);
                          } else {
                            setInteriorDesignSpacesOverride(Math.max(0, parseInt(val) || 0));
                          }
                        }}
                      />
                      {interiorDesignSpacesOverride !== null && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => setInteriorDesignSpacesOverride(null)}
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Per-Space Design Status */}
                  {effectiveInteriorSpaceCount > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                      <Label className="text-sm font-medium">Per-Space Design Status</Label>
                      <div className="space-y-2">
                        {interiorDesignSpaces.map((space, idx) => (
                          <div key={space.id} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                            <span className="text-sm font-medium w-20 text-muted-foreground">Space {idx + 1}</span>
                            <span className="text-muted-foreground">→</span>
                            <Select 
                              value={space.completeness_level} 
                              onValueChange={(v) => updateInteriorDesignSpace(space.id, "completeness_level", v)}
                            >
                              <SelectTrigger className="flex-1 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DESIGN_COMPLETENESS_LEVELS.map(level => (
                                  <SelectItem key={level.value} value={level.value}>
                                    <div className="flex items-center justify-between w-full gap-4">
                                      <span>{level.shortLabel}</span>
                                      <span className={`text-xs ${level.fee > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                        {level.fee > 0 ? `+$${level.fee.toLocaleString()}` : '$0'}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Design Guidance Collapsible */}
                  <Collapsible open={showDesignGuidance} onOpenChange={setShowDesignGuidance}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <HelpCircle className="w-4 h-4" />
                          How to Assess Interior Design Completeness
                        </span>
                        {showDesignGuidance ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-3">
                      <div className="space-y-4 text-sm">
                        {DESIGN_COMPLETENESS_LEVELS.map(level => (
                          <div key={level.value} className="p-3 bg-muted/50 rounded-lg space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{level.label}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${level.fee > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {level.fee > 0 ? `+$${level.fee.toLocaleString()}/space` : 'No additional fee'}
                              </span>
                            </div>
                            <p className="text-muted-foreground text-xs">{level.description}</p>
                            <div className="text-xs">
                              <span className="font-medium text-muted-foreground">Typical assets:</span>
                              <ul className="list-disc list-inside text-muted-foreground mt-1">
                                {level.assets.slice(0, 3).map((asset, i) => (
                                  <li key={i}>{asset}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Internal Notes</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes visible only to your team..." rows={3} />
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Premium Quote Summary */}
          <div className="space-y-4">
            <Card className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] bg-[#000000] rounded-[24px]" style={{ scrollbarColor: 'rgba(255,255,255,0.12) transparent', scrollbarWidth: 'thin' }}>
              <CardHeader className="pb-4 px-6 pt-6 border-b border-white/[0.06]">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-[17px] font-light text-white tracking-tight leading-tight">Quote Summary</h2>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-white/35">Visualization</span>
                </div>
              </CardHeader>
              <CardContent className="px-6 py-2 text-sm">
                {calculations.interiorModelingSubtotal > 0 && (
                  <div className="flex items-baseline justify-between py-3.5 border-b border-white/[0.06]">
                    <span className="text-[14px] text-white/80 tracking-tight">Interior Modeling</span>
                    <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.interiorModelingSubtotal.toLocaleString()}</span>
                  </div>
                )}

                {exteriorCalc.exteriorModelingTotal > 0 && (
                  <div className="py-4 border-b border-white/[0.06]">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[14px] text-white font-medium tracking-tight">Exterior Modeling</span>
                      <span className="text-[15px] text-white font-semibold tabular-nums">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</span>
                    </div>
                    <div className="pl-1 space-y-1.5 text-[12px]">
                      {exteriorCalc.primaryStructureSubtotal > 0 && <div className="flex justify-between"><span className="text-white/45">Primary Structures · {exteriorCalc.totalUniqueStructures}</span><span className="text-white/65 tabular-nums">${exteriorCalc.primaryStructureSubtotal.toLocaleString()}</span></div>}
                      {exteriorCalc.replicationSubtotal > 0 && <div className="flex justify-between"><span className="text-white/45">Repeated Placements · {exteriorCalc.totalReplicatedPlacements}</span><span className="text-white/65 tabular-nums">${exteriorCalc.replicationSubtotal.toLocaleString()}</span></div>}
                      {exteriorCalc.siteEnvironmentCost > 0 && <div className="flex justify-between"><span className="text-white/45">Site Environment</span><span className="text-white/65 tabular-nums">${exteriorCalc.siteEnvironmentCost.toLocaleString()}</span></div>}
                      {exteriorCalc.realityCaptureCost > 0 && <div className="flex justify-between"><span className="text-white/45">Reality Capture</span><span className="text-white/65 tabular-nums">${exteriorCalc.realityCaptureCost.toLocaleString()}</span></div>}
                    </div>
                  </div>
                )}

                {calculations.renderingSubtotal > 0 && (
                  <div className="flex items-baseline justify-between py-3.5 border-b border-white/[0.06]">
                    <span className="text-[14px] text-white/80 tracking-tight">Renderings</span>
                    <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.renderingSubtotal.toLocaleString()}</span>
                  </div>
                )}

                {calculations.vrSubtotal > 0 && (
                  <div className="flex items-baseline justify-between py-3.5 border-b border-white/[0.06]">
                    <span className="text-[14px] text-white/80 tracking-tight">VR / 360° Tours</span>
                    <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.vrSubtotal.toLocaleString()}</span>
                  </div>
                )}

                {calculations.animationSubtotal > 0 && (
                  <div className="py-3.5 border-b border-white/[0.06]">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[14px] text-white/80 tracking-tight">
                        {animationType === "ai" ? "AI Motion" : "Cinematic Film"}
                      </span>
                      <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.animationSubtotal.toLocaleString()}</span>
                    </div>
                    <p className="text-[11px] text-white/40 pl-1">
                      {animationDuration}s · {calculations.animationShots} shots
                      {socialMediaPack ? " · incl. Social Media Pack" : ""}
                    </p>
                  </div>
                )}

                {calculations.interiorDesignFee > 0 && (
                  <div className="py-3.5 border-b border-white/[0.06]">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[14px] text-white/80 tracking-tight">Interior Design Direction</span>
                      <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.interiorDesignFee.toLocaleString()}</span>
                    </div>
                    <p className="text-[11px] text-white/40 pl-1">{interiorDesignSpaces.filter(s => s.completeness_level !== 'full_package').length} space(s) requiring design work</p>
                  </div>
                )}

                {/* Estimated Production Timeline */}
                <TimelineEstimateWidget
                  deliverables={timelineDeliverables}
                  proposalTotal={calculations.grandTotal}
                  data-testid="viz-timeline-widget"
                />

                {/* Visualization Payment Plan */}
                {calculations.grandTotal > 0 && (
                <div className="mt-5 p-5 bg-[#000000] border border-white/[0.06] rounded-[20px]" data-testid="viz-payment-plan">
                  <div className="flex items-baseline justify-between mb-1">
                    <h3 className="text-[13px] font-semibold text-white tracking-tight">Payment Plan</h3>
                    <span className="text-[10px] uppercase tracking-[0.1em] text-white/35">Visualization</span>
                  </div>
                  <p className="text-[11px] text-white/45 mb-5">Milestone-based schedule</p>

                  <div className="mb-5 pb-5 border-b border-white/[0.06]">
                    <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">Visualization Scope</p>
                    <p className="text-[24px] font-light text-white tabular-nums tracking-tight leading-none">
                      ${visualizationScopeTotal.toLocaleString()}
                    </p>
                  </div>

                  <div className="mb-5">
                    <p className="text-[11px] text-white/60 mb-2.5">Structure</p>
                    <Select value={paymentPreset} onValueChange={setPaymentPreset}>
                      <SelectTrigger className="h-10 bg-white/[0.02] border-white/[0.08] text-white text-[12px] rounded-[10px]" data-testid="viz-payment-preset-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(PAYMENT_PRESETS).map(([key, preset]) => (
                          <SelectItem key={key} value={key}>{preset.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2.5">
                    {vizMilestoneAmounts.map((milestone, idx) => (
                      <div key={idx} className="flex justify-between items-baseline">
                        <span className="text-[12px] text-white/55">{milestone.label}</span>
                        <span className="text-[13px] text-white font-medium tabular-nums">${milestone.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Grand Total */}
                <div className="mt-5 pt-5 border-t border-white/[0.08]">
                  <div className="p-4 bg-[#000000] border border-white/[0.08] rounded-[16px]">
                    <div className="flex items-baseline justify-between">
                      <span className="text-[11px] uppercase tracking-[0.1em] text-white/50">Total Investment</span>
                      <span className="text-[22px] font-light text-white tabular-nums tracking-tight">${calculations.grandTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <Button
                  className="w-full gap-2 mt-4 bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.08] rounded-[12px] h-10"
                  variant="outline"
                  onClick={handleGenerateProposal}
                  disabled={generatingProposal || !quoteId || isNew}
                  data-testid="generate-proposal-btn"
                >
                  <FileText className="w-4 h-4" />
                  {generatingProposal ? "Generating..." : "Generate Proposal"}
                </Button>
                {isNew && (
                  <p className="text-[11px] text-white/40 text-center mt-2">
                    Save the quote first to generate a proposal
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Proposal Modal */}
      {proposalHtml && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Generated Proposal</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrintProposal}>
                  <FileText className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setProposalHtml(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                srcDoc={proposalHtml}
                className="w-full h-full min-h-[600px]"
                title="Proposal Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
